import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { CURRENT_PIPELINE, Pipeline, USER_PIPELINES_TOKEN } from '@vendasta/sales-common';
import { Opportunity, StageInterface } from '@vendasta/sales-opportunities';
import { combineLatest, firstValueFrom, Observable, startWith } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { SortSelection } from '../../pipeline-board-service/interfaces';
import { PipelineBoardService, SORT_DIRECTIONS } from '../../pipeline-board-service/pipeline-board.service';
import { PipelineField, PipelineSort } from '../../pipeline-sort';
import { PipelineStageStats, PipelineStats } from '../../pipeline-stats/pipeline-stats.service';
import {
  PIPELINE_SETTINGS_ACCESS_TOKEN,
  DEFAULT_CURRENCY_CODE_TOKEN,
  PARTNER_ID_TOKEN,
  PIPELINE_SETTINGS_URL_TOKEN,
} from '../../tokens';
import { FeatureFlagService } from '@galaxy/partner';
import { Router } from '@angular/router';
@Component({
  selector: 'sales-ui-pipeline-board-filtered',
  templateUrl: './pipeline-board-filtered.component.html',
  styleUrls: ['./pipeline-board-filtered.component.scss'],
})
export class PipelineBoardFilteredComponent {
  @Output() sortAction = new EventEmitter<PipelineSort>();
  @Output() draggedOpportunityId = new EventEmitter<string>();
  @Output() selectedOpportunity = new EventEmitter<Opportunity>();
  @Output() sortDirection = new EventEmitter<PipelineSort>();
  @Output() changePipeline = new EventEmitter<string>();

  protected readonly currency$: Observable<string> = this.defaultCurrencyCode$;
  protected readonly statsMap = new Map<string, Observable<PipelineStageStats>>();

  protected readonly pipelineField = PipelineField;
  protected readonly sortSelection$: Observable<SortSelection>;
  protected readonly sortDirection$: Observable<SORT_DIRECTIONS>;
  protected readonly sortDirectionIcon$: Observable<string>;

  protected readonly opportunitiesVisibleCount$ = this.pipelineStats.totalVisibleOpportunitiesForAllStages$.pipe(
    shareReplay(1),
  );
  protected readonly stages$: Observable<StageInterface[]>;
  private readonly pipelineSettingsFeatureFlag = 'pcc_pipeline_settings';
  protected readonly hasAccessToPipelineSettings$: Observable<boolean>;

  constructor(
    private readonly pipelineStats: PipelineStats,
    @Inject(USER_PIPELINES_TOKEN) protected readonly pipelines$: Observable<Pipeline[]>,
    @Inject(DEFAULT_CURRENCY_CODE_TOKEN) private readonly defaultCurrencyCode$: Observable<string>,
    @Inject(PIPELINE_SETTINGS_URL_TOKEN) private readonly pipelineSettingsURL: string,
    @Inject(PIPELINE_SETTINGS_ACCESS_TOKEN) private readonly canAccessPipelineSettings$: Observable<boolean>,
    @Inject(CURRENT_PIPELINE) protected readonly currentPipeline$: Observable<Pipeline>,
    @Inject(PARTNER_ID_TOKEN) private readonly partnerId$: Observable<string>,
    private readonly pipelineBoardService: PipelineBoardService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly router: Router,
  ) {
    const hasPipelineSettingsFeatureFlag$ = this.partnerId$.pipe(
      switchMap((partnerId) =>
        this.featureFlagService.batchGetStatus(partnerId, '', [this.pipelineSettingsFeatureFlag]),
      ),
      map((featureFlags) => featureFlags?.[this.pipelineSettingsFeatureFlag] || false),
      startWith(false),
    );

    this.hasAccessToPipelineSettings$ = combineLatest([
      hasPipelineSettingsFeatureFlag$,
      this.canAccessPipelineSettings$,
    ]).pipe(
      map(([featureFlag, hasAccess]) => featureFlag && !!this.pipelineSettingsURL && hasAccess),
      startWith(false),
    );

    this.sortSelection$ = this.pipelineBoardService.sortSelection$;
    this.sortDirection$ = this.pipelineBoardService.defaultSortDirection$;

    this.sortDirectionIcon$ = this.sortDirection$.pipe(
      map((defaultSortDirection) => (defaultSortDirection ? 'arrow_downwards' : 'arrow_upwards')),
    );

    this.stages$ = this.currentPipeline$.pipe(map((pipeline) => pipeline.stages));
    const root = document.documentElement;

    this.stages$.subscribe((stages) => {
      root.style.setProperty('--stage-count', `${stages.length}`);
      for (const stage of stages) {
        this.statsMap.set(stage.stageId, this.pipelineStats.getStatsForStage$(stage.stageId));
      }
    });

    this.pipelineBoardService.applyFiltersFromQueryParams();
  }

  protected changeSelectedPipeline(pipelineID: string): void {
    this.changePipeline.emit(pipelineID);
  }

  protected async openPipelineSettings(): Promise<void> {
    const hasAccess = await firstValueFrom(this.canAccessPipelineSettings$);
    if (hasAccess) {
      this.router.navigateByUrl(this.pipelineSettingsURL);
    }
  }

  protected sortActions(action: PipelineField): void {
    this.sortAction.emit(<PipelineSort>{ active: action });
  }

  protected dropOpportunity($event: string): void {
    this.draggedOpportunityId.emit($event);
  }

  protected changeSortDirection(): void {
    this.sortDirection.emit();
  }

  protected handleOpportunitySelected($event: Opportunity): void {
    this.selectedOpportunity.emit($event);
  }
}
